import TodayIcon from '@mui/icons-material/Today'
import WarningIcon from '@mui/icons-material/Warning'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import I18n from 'i18n-js'
import moment from 'moment'
import { Fragment } from 'react'

import cloudinary from '@src/lib/Cloudinary'

import { formatWithCents } from '../../lib/Utils'
import LegacySvg from '../shared/LegacySvg'

import CarouselPreview from './CarouselPreview'
import ExternalVideoPreview from './ExternalVideoPreview'
import { filterLocalAdsForShare } from './localAdsTotals'
import StatRow from './StatRow'
import { statsCounter } from './statsTotals'

const FacebookStats = ({ share, stats = {}, localAds }) => {
  const adData = filterLocalAdsForShare({ share, localAds })
  const { actions = {}, views = {}, clicks = {} } = stats
  return (
    <>
      <TableCell>
        <Tooltip
          disableHoverListener={adData.adRuns.length ? false : true}
          title={
            <>
              {adData.adRuns.map((adRun) => (
                <StatRow
                  label={`${moment(adRun.startDate).format('L')} - ${moment(
                    adRun.endDate,
                  ).format('L')}`}
                  value={formatWithCents.format(adRun.total / 100)}
                />
              ))}
            </>
          }
        >
          <Box display="flex" justifyContent="center">
            <Box
              bgcolor="success.main"
              borderRadius={1}
              color="white"
              display="flex"
              justifyContent="center"
              minWidth="40px"
              padding={2}
            >
              {formatWithCents.format(adData.total / 100)}
            </Box>
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Tooltip
          title={
            <>
              <StatRow
                label={I18n.t('reporting.organic')}
                value={I18n.l('number', views.impressions_organic || 0)}
              />
              <StatRow
                label={I18n.t('reporting.paid')}
                value={I18n.l('number', views.impressions_paid || 0)}
              />
            </>
          }
        >
          <span>
            {statsCounter(views) !== null
              ? I18n.l('number', statsCounter(views))
              : '-'}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Tooltip
          title={
            <>
              <StatRow
                label={I18n.t('reporting.likes')}
                value={I18n.l('number', actions.post_reaction_like || 0)}
              />
              <StatRow
                label={I18n.t('reporting.comments')}
                value={I18n.l('number', actions.comments || 0)}
              />
              <StatRow
                label={I18n.t('reporting.shares')}
                value={I18n.l('number', actions.posts || 0)}
              />
            </>
          }
        >
          <span>
            {statsCounter(actions) !== null
              ? I18n.l('number', statsCounter(actions))
              : '-'}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        {statsCounter(clicks) !== null
          ? I18n.l('number', statsCounter(clicks))
          : '-'}
      </TableCell>
      <TableCell align="center">
        <Button href={share.post_url} target="_blank">
          {I18n.t('reporting.viewFacebook')}
        </Button>
      </TableCell>
    </>
  )
}

const TwitterStats = ({ share, stats = {} }) => {
  const { actions = {}, clicks = {} } = stats
  return (
    <>
      <TableCell />
      <TableCell align="center">
        <Tooltip
          title={
            <>
              <StatRow
                label={I18n.t('reporting.favorites')}
                value={I18n.l('number', actions.favorites || 0)}
              />
              <StatRow
                label={I18n.t('reporting.retweets')}
                value={I18n.l('number', actions.retweets || 0)}
              />
            </>
          }
        >
          <span>
            {statsCounter(actions) !== null
              ? I18n.l('number', statsCounter(actions))
              : '-'}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        {statsCounter(clicks) !== null
          ? I18n.l('number', statsCounter(clicks))
          : '-'}
      </TableCell>
      <TableCell align="center">
        <Button href={share.tweet_url} target="_blank">
          {I18n.t('reporting.viewTwitter')}
        </Button>
      </TableCell>
    </>
  )
}

const EmailStats = ({ stats = {} }) => {
  const { email = {}, views = {}, clicks = {} } = stats
  return (
    <>
      <TableCell />
      <TableCell align="center">
        {statsCounter(views) !== null
          ? I18n.l('number', statsCounter(views))
          : '-'}
      </TableCell>
      <TableCell align="center">
        {I18n.l('number', email.bounced || 0)}
      </TableCell>
      <TableCell align="center">
        {statsCounter(clicks) !== null
          ? I18n.l('number', statsCounter(clicks))
          : '-'}
      </TableCell>
    </>
  )
}

const InstagramStats = ({ stats = {} }) => {
  const { views = {}, actions = {} } = stats
  return (
    <>
      <TableCell />
      <TableCell align="center">
        {statsCounter(views) !== null
          ? I18n.l('number', statsCounter(views))
          : '-'}
      </TableCell>
      <TableCell align="center">
        {statsCounter(actions) !== null
          ? I18n.l('number', statsCounter(actions))
          : '-'}
      </TableCell>
    </>
  )
}

const EmptyStats = ({ channel }) => {
  let numBlank = 3
  if (channel === 'facebook') {
    numBlank = 4
  }
  if (channel === 'instagram') {
    numBlank = 2
  }

  return (
    <Fragment>
      {new Array(numBlank).fill().map((x, i) => (
        <TableCell
          align="center"
          key={i} /* eslint-disable-line react/no-array-index-key */
        >
          <Typography color="textSecondary">-</Typography>
        </TableCell>
      ))}
      <TableCell align="center">
        <Tooltip title={I18n.t('reporting.errorWhileSharing')}>
          <WarningIcon color="error" />
        </Tooltip>
      </TableCell>
    </Fragment>
  )
}

const ScheduledStats = ({ channel, share }) => {
  let numBlank = 3
  if (channel === 'facebook') {
    numBlank = 4
  }
  if (channel === 'instagram') {
    numBlank = 2
  }

  return (
    <Fragment>
      {new Array(numBlank).fill().map((x, i) => (
        <TableCell
          align="center"
          key={i} /* eslint-disable-line react/no-array-index-key */
        >
          <Typography color="textSecondary">-</Typography>
        </TableCell>
      ))}
      <TableCell align="center">
        <Tooltip
          title={I18n.t('reporting.shareIsScheduled', {
            date: moment(share.scheduled_for).format('L'),
          })}
        >
          <TodayIcon />
        </Tooltip>
      </TableCell>
    </Fragment>
  )
}

const OrganicCampaignDetailsListItems = ({ campaign, channel, stats }) => {
  const { localAds } = campaign

  let shares = campaign[channel]

  //Filter out share_mode === 'mobile', we cannot get reporting for them.
  if (channel === 'instagram') {
    shares = shares.filter((share) => share.share_mode === 'manual')
  }

  return shares.map((share) => {
    let statsChild = null
    const shareStats = stats.find((stat) => {
      return String(share.id) === String(stat.share_id) //Normalize to string to ensure apples to apples comparison
    })

    if (share.status === 'failure') {
      statsChild = <EmptyStats channel={channel} />
    } else if (share.scheduled_for && share.executed_at == null) {
      statsChild = <ScheduledStats channel={channel} share={share} />
    } else {
      switch (channel) {
        case 'facebook':
          statsChild = (
            <FacebookStats
              localAds={localAds}
              share={share}
              stats={shareStats}
            />
          )
          break
        case 'twitter':
          statsChild = <TwitterStats share={share} stats={shareStats} />
          break
        case 'email':
          statsChild = <EmailStats share={share} stats={shareStats} />
          break
        case 'instagram':
          statsChild = <InstagramStats share={share} stats={shareStats} />
          break
        default:
          break
      }
    }

    const { post_format } = share.share_setting
    return (
      <TableRow key={share.id}>
        {channel === 'facebook' && (
          <TableCell>
            <LegacySvg
              height="36px"
              symbol={post_format === 'carousel' ? 'ad-carousel' : 'ad-image'}
              width="36px"
            />
          </TableCell>
        )}
        <TableCell>
          <ShareImage campaign={campaign} share={share} />
        </TableCell>
        <TableCell>
          {share.executed_at
            ? moment(share.executed_at).format('L')
            : I18n.t('reporting.scheduledFor', {
                date: moment(share.scheduled_for).format('L'),
              })}
        </TableCell>
        <TableCell>{share.share_setting.name}</TableCell>
        {statsChild}
      </TableRow>
    )
  })
}

function ShareImage({ campaign, share }) {
  const { share_setting } = share
  if (share_setting.post_format === 'carousel') {
    return <CarouselPreview shareSetting={share_setting} />
  }
  if (
    share_setting.post_format === 'full_html' ||
    share_setting.post_format === 'carousel' ||
    share_setting.post_format === 'photo' ||
    share_setting.post_format === 'template' ||
    share_setting.post_format === 'template' ||
    (share_setting.post_format === 'link' &&
      !share_setting.digital_assets[0].external_video_url)
  ) {
    let shareSettingsUrl = share_setting.responsive_share_image_url

    if (
      share_setting.digital_assets &&
      share_setting.digital_assets[0] &&
      share_setting.digital_assets[0].file &&
      share_setting.digital_assets[0].file.public_id
    ) {
      shareSettingsUrl = cloudinary
        .instance()
        .url(`${share_setting.digital_assets[0].file.public_id}.jpg`, {
          width: 220,
          height: 147,
          crop: 'fill',
          format: 'jpg',
          quality: 'auto',
        })
    }
    return (
      <img
        alt={campaign.brand.name}
        src={shareSettingsUrl}
        style={{
          width: 220,
          height: 147,
          objectFit: 'cover',
          verticalAlign: 'middle',
        }}
        title={campaign.brand.name}
      />
    )
  }
  if (share_setting.post_format === 'video') {
    const [digital_asset] = share_setting.digital_assets || []
    return (
      <Box
        component={'video'}
        controls="controls"
        height={147}
        src={digital_asset && digital_asset.download_url}
        width={220}
      />
    )
  }
  if (share_setting.post_format === 'link') {
    return (
      <ExternalVideoPreview
        shareSetting={share_setting.digital_assets[0].external_video_url}
        width={220}
        height={147}
      />
    )
  }
  return null
}

export default OrganicCampaignDetailsListItems
